<template>
  <v-container>
    <v-card>
      <v-data-table
        :headers="headers"
        :items="pricing"
        :items-per-page="50"
        item-key="id"
        :search="search"
        ref="refName"
      >
        <template v-slot:top>
          <v-container fluid>
            <v-row>
              <v-col cols="12 text-left">
                <strong class="headline headline-pad">Filters</strong>
                <v-btn color="primary" fab small dark @click.stop="showFilter = !showFilter">
                  <v-icon>mdi-filter-menu-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
          <v-container fluid v-if="showFilter">
            <v-row>
              <v-col cols="4">
                <v-text-field v-model="companyName" type="text" label="Company Name"></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model="planName" type="text" label="Plan Name"></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model="cta" type="text" label="Call To Action"></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="8">
                <v-range-slider
                  v-model="defaultPriceRange"
                  :max="defaultPriceMax"
                  :min="defaultPriceMin"
                  hide-details
                  class="align-center"
                >
                  <template v-slot:prepend>
                    <v-text-field
                      :value="defaultPriceRange[0]"
                      class="mt-0 pt-0"
                      hide-details
                      single-line
                      type="number"
                      style="width: 100px"
                      prepend-icon="mdi-currency-usd"
                      @change="$set(defaultPriceRange, 0, $event)"
                    ></v-text-field>
                  </template>
                  <template v-slot:append>
                    <v-text-field
                      :value="defaultPriceRange[1]"
                      class="mt-0 pt-0"
                      hide-details
                      single-line
                      type="number"
                      style="width: 100px"
                      prepend-icon="mdi-currency-usd"
                      @change="$set(defaultPriceRange, 1, $event)"
                    ></v-text-field>
                  </template>
                </v-range-slider>
                <!-- <v-text-field v-model="defaultPrice" type="text" label="Default Price"></v-text-field> -->
              </v-col>
              <v-col cols="4">
                <v-switch
                  v-model="showPlansLargerThan1000"
                  :label="`Show plans larger than $1,000`"
                  inset
                ></v-switch>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="2">
                <v-switch v-model="showTrialPlans" :label="`Plans with trials`" inset></v-switch>
              </v-col>
              <v-col cols="2">
                <v-switch v-model="showEnterprisePlans" :label="`Enterprise plans`" inset></v-switch>
              </v-col>
              <v-col cols="2">
                <v-switch v-model="showPerUserPlans" :label="`Per user pricing`" inset></v-switch>
              </v-col>
              <v-col cols="2">
                <v-switch v-model="showSlidingPlans" :label="`Sliding scale pricing`" inset></v-switch>
              </v-col>
              <v-col cols="2">
                <v-switch v-model="showMoneyBackPlans" :label="`Money back guarantee`" inset></v-switch>
              </v-col>
            </v-row>
          </v-container>
        </template>
        <template v-slot:item.company.name="{ item }">
          <v-container class="fill-height" fluid>
            <div justify="left">
              <v-list-item three-line>
                <v-list-item-avatar tile size="60" color="grey">
                  <v-img :src="'//logo.clearbit.com/'+item.company.domain + '?greyscale'"></v-img>
                </v-list-item-avatar>
                <v-list-item-content>
                  <div class="overline mb-4">{{item.company.domain}}</div>
                  <v-list-item-title class="headline mb-1">{{ item.company.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </div>
          </v-container>
        </template>
        <template v-slot:item.plan_name="{ item }">
          <v-container class="fill-height" fluid>
            <div justify="left">
              <p class="headline blue--text font-weight-light">
                <v-icon v-if="item.hero == 1" class="ma-1 hero-icon yellow--text">mdi-star</v-icon>
                {{ item.plan_name }}
              </p>
              <div class="d-block text-left" v-if="item.hero == 1">
                <p class="overline text--primary font-weight-light">HERO</p>
              </div>
              <div class="d-block text-left" v-else>
                <p class="overline text--primary font-weight-light">PLAN</p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.call_to_action="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <v-btn>{{ item.call_to_action }}</v-btn>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.default_price="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="headline text--primary font-weight-light text-center">
                  <span v-if="item.default_price==null">Contact</span>
                  <span v-else>${{ item.default_price }}</span>
                </p>
              </div>
              <div class="d-block text-center">
                <p class="overline text--primary font-weight-light text-center">
                  <span v-if="item.default_price!=''">{{ item.default_period }}</span>
                </p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.secondary_price="{ item }">
          <v-container class="fill-height" fluid>
            <div v-if="item.secondary_price !== null">
              <div class="d-block">
                <p
                  class="headline text--primary font-weight-light text-center"
                >${{ item.secondary_price }}</p>
              </div>
              <div class="d-block text-center">
                <p
                  class="overline text--primary font-weight-light text-center"
                >{{ item.secondary_period }}</p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.length_of_trial="{ item }">
          <v-container class="fill-height" fluid>
            <div v-if="item.length_of_trial != ''">
              <div class="d-block">
                <p
                  class="headline text--primary font-weight-light text-center"
                >{{ item.length_of_trial }}</p>
              </div>
              <div class="d-block text-center">
                <p
                  class="overline text--primary font-weight-light text-center"
                >{{item.trial_period}}</p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.currency="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="text--primary font-weight-light text-center">{{ item.currency }}</p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.enterprise="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="headline text--primary font-weight-light text-center">
                  <v-icon
                    x-large
                    class="green--text text--accent-3"
                    v-if="item.enterprise != 0"
                  >mdi-check</v-icon>
                  <v-icon x-large class="red--text text--accent-3" v-else>mdi-close</v-icon>
                </p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.per_user_pricing="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="headline text--primary font-weight-light text-center">
                  <v-icon
                    x-large
                    class="green--text text--accent-3"
                    v-if="item.per_user_pricing != 0"
                  >mdi-check</v-icon>
                  <v-icon x-large class="red--text text--accent-3" v-else>mdi-close</v-icon>
                </p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.sliding_scale_pricing="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="headline text--primary font-weight-light text-center">
                  <v-icon
                    x-large
                    class="green--text text--accent-3"
                    v-if="item.sliding_scale_pricing != 0"
                  >mdi-check</v-icon>
                  <v-icon x-large class="red--text text--accent-3" v-else>mdi-close</v-icon>
                </p>
              </div>
            </div>
          </v-container>
        </template>
        <template v-slot:item.money_back_guarantee="{ item }">
          <v-container class="fill-height" fluid>
            <div>
              <div class="d-block">
                <p class="headline text--primary font-weight-light text-center">
                  <v-icon
                    x-large
                    class="green--text text--accent-3"
                    v-if="item.money_back_guarantee != 0"
                  >mdi-check</v-icon>
                  <v-icon x-large class="red--text text--accent-3" v-else>mdi-close</v-icon>
                </p>
              </div>
            </div>
          </v-container>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "PricingTable",

  data: () => ({
    cta: "",
    planName: "",
    companyName: "",
    search: "",
    defaultPriceRange: [0, 1000],
    defaultPriceMax: 1000,
    realMaxPrice: 0,
    defaultPriceMin: 0,
    filteredItems: 0,
    pricing: [],
    stats: {},
    showPlansLargerThan1000: false,
    showTrialPlans: false,
    showEnterprisePlans: false,
    showPerUserPlans: false,
    showSlidingPlans: false,
    showMoneyBackPlans: false,
    showFilter: false
  }),
  mounted() {
    this.getPricing();
    this.getStats();
  },
  updated() {
    this.getFilteredCount();
  },
  computed: {
    headers() {
      return [
        {
          text: "Company",
          align: "start",
          sortable: true,
          value: "company.name",
          filter: value => {
            if (!this.companyName) return true;
            // console.log(value);
            return value.toUpperCase().includes(this.companyName.toUpperCase());
          }
        },
        {
          text: "Plan",
          value: "plan_name",
          filter: value => {
            if (!this.planName) return true;
            // console.log(value);
            return value.toUpperCase().includes(this.planName.toUpperCase());
          }
        },
        {
          text: "Call To Action",
          value: "call_to_action",
          filter: value => {
            if (!this.cta) return true;
            // console.log(value);
            return value.toUpperCase().includes(this.cta.toUpperCase());
          }
        },
        {
          text: "Default Pricing",
          value: "default_price",
          filter: value => {
            return (
              value > this.defaultPriceRange[0] &&
              value < this.defaultPriceRange[1]
            );
          }
        },
        { text: "Secondary Pricing", value: "secondary_price" },
        {
          text: "Trial",
          value: "length_of_trial",
          filter: value => {
            if (!this.showTrialPlans) return true;
            return value != "";
          }
        },
        {
          text: "Currency",
          value: "currency",
          align: " d-none"
        },
        {
          text: "Enterprise",
          value: "enterprise",
          filter: value => {
            if (!this.showEnterprisePlans) return true;
            return value != false;
          },
          align: " d-none"
        },
        {
          text: "Per User Pricing",
          value: "per_user_pricing",
          filter: value => {
            if (!this.showPerUserPlans) return true;
            return value != false;
          },
          align: " d-none"
        },
        {
          text: "Sliding Pricing Scale",
          value: "sliding_scale_pricing",
          filter: value => {
            if (!this.showSlidingPlans) return true;
            return value != false;
          },
          align: " d-none"
        },
        {
          text: "Money Back Guarantee",
          value: "money_back_guarantee",
          filter: value => {
            if (!this.showMoneyBackPlans) return true;
            return value != false;
          },
          align: " d-none"
        }
      ];
    },
    showFreePlans() {
      return this.$store.getters.free;
    }
  },
  watch: {
    showPlansLargerThan1000(value) {
      console.log(this.showPlansLargerThan1000 + " vs " + value);
      if (this.showPlansLargerThan1000) {
        this.defaultPriceMax = this.realMaxPrice;
        this.defaultPriceRange[(this.defaultPriceRange, this.defaultPriceMax)];
      } else {
        this.defaultPriceMax = 1000;
        this.defaultPriceRange[(this.defaultPriceRange, 1000)];
      }
    },
    currencies: {
      deep: true,
      handler: function(value) {
        console.log(value);
      }
    }
  },
  methods: {
    getPricing() {
      if (this.pricing == "undefined" || this.pricing.length < 1) {
        this.$store.dispatch("retrievePricing").then(() => {
          this.pricing = this.$store.getters.pricing;
        });
      }
    },
    getFilteredCount() {
      if (this.$refs.refName !== "undefined") {
        this.filteredItems = this.$refs.refName.items.length;
      } else {
        this.filteredItems = 0;
      }
    },

    hero(isHero) {
      return isHero == "1" ? "blue-grey" : "";
    },
    getStats() {
      this.$store.dispatch("retrieveStats").then(() => {
        this.stats = this.$store.getters.stats;
        this.realMaxPrice = this.stats.max_price;
        this.defaultPriceMin = this.stats.min_price;
      });
    }
  }
};
</script>

<style scoped>
.hero-icon {
  margin-top: 2px !important;
}
.headline-pad {
  margin-right: 10px;
}
</style>
